import type { UserProgramStatus } from './user-program-statuses.js';
import { UserProgramStatuses } from './user-program-statuses.js';

/**
 * Coarse beneficiary lifecycle bucket for UX (portfolio, menu).
 * Maps from {@link UserProgramStatus}.
 */
export const ProgramStates = {
  PENDING: 'pending',
  ONGOING: 'ongoing',
  PROCESSED: 'processed',
} as const;

export type ProgramState = (typeof ProgramStates)[keyof typeof ProgramStates];

export const PENDING_PROGRAM_STATUSES: readonly UserProgramStatus[] = [
  UserProgramStatuses.NOT_STARTED,
  UserProgramStatuses.COMING_UP,
];

export const ONGOING_PROGRAM_STATUSES: readonly UserProgramStatus[] = [
  UserProgramStatuses.IN_PROGRESS,
  UserProgramStatuses.EXTENDED,
];

/**
 * Short labels used when an ongoing program status appears next to a
 * beneficiary identity (e.g. recommendation modal beneficiary picker).
 * Distinct from {@link USER_PROGRAM_STATUSES_LABELS}, which targets the
 * back-office program-status column.
 */
export const PROGRAM_STATUSES_LABELS: Record<UserProgramStatus, string> = {
  [UserProgramStatuses.NOT_STARTED]: 'Non démarré',
  [UserProgramStatuses.COMING_UP]: 'À venir',
  [UserProgramStatuses.IN_PROGRESS]: 'Presta en cours',
  [UserProgramStatuses.EXTENDED]: 'Prolongée',
  [UserProgramStatuses.NON_ADHESION]: 'Non adhésion',
  [UserProgramStatuses.ARCHIVED]: 'Archivé',
};

export const PROCESSED_PROGRAM_STATUSES: readonly UserProgramStatus[] = [
  UserProgramStatuses.NON_ADHESION,
  UserProgramStatuses.ARCHIVED,
];

export function getProgramStateFromStatus(
  status: UserProgramStatus | null | undefined,
): ProgramState {
  if (status == null) {
    return ProgramStates.PENDING;
  }

  if (PENDING_PROGRAM_STATUSES.includes(status)) {
    return ProgramStates.PENDING;
  }

  if (ONGOING_PROGRAM_STATUSES.includes(status)) {
    return ProgramStates.ONGOING;
  }

  return ProgramStates.PROCESSED;
}

/**
 * When several programs exist, priority is ongoing → pending → processed
 * so the “most active” experience drives menu and routing.
 */
export function getProgramStateFromUserPrograms(
  programs: { status?: UserProgramStatus | null }[],
): ProgramState {
  if (!programs.length) {
    return ProgramStates.PENDING;
  }

  const buckets = programs.map((p) => getProgramStateFromStatus(p.status));

  if (buckets.includes(ProgramStates.ONGOING)) {
    return ProgramStates.ONGOING;
  }

  if (buckets.includes(ProgramStates.PENDING)) {
    return ProgramStates.PENDING;
  }

  return ProgramStates.PROCESSED;
}

export function isProgramPending(state: ProgramState): boolean {
  return state === ProgramStates.PENDING;
}

export function isProgramOngoing(state: ProgramState): boolean {
  return state === ProgramStates.ONGOING;
}

export function isProgramProcessed(state: ProgramState): boolean {
  return state === ProgramStates.PROCESSED;
}
